import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Attach auth token to every request
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem("auth_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 responses — redirect to login
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("auth_token");
      localStorage.removeItem("auth_email");
      window.location.href = "/";
    }
    return Promise.reject(error);
  }
);

// A value with a comma in it ("Weil, Gotshal & Manges") would be split apart
// by a comma-joined list, so such lists go as a JSON array instead.
const stringifyList = (values) => {
  if (!values?.length) return undefined;
  return values.some((v) => String(v).includes(",")) ? JSON.stringify(values) : values.join(",");
};

const stringifyDynamic = (df) => {
  if (!df || Object.keys(df).length === 0) return undefined;
  const nonEmpty = {};
  for (const [k, v] of Object.entries(df)) {
    if (v?.length) nonEmpty[k] = v;
  }
  return Object.keys(nonEmpty).length ? JSON.stringify(nonEmpty) : undefined;
};

export const hubspotInstallEntryUrl = `${API}/hubspot/oauth/install`;

export async function fetchInstallUrl() {
  const response = await axios.get(`${API}/hubspot/oauth/install-url`);
  return response.data;
}

export async function fetchArrAutomationConfig() {
  const response = await axios.get(`${API}/hubspot/arr-automation/config`);
  return response.data;
}

export async function fetchConnections() {
  const response = await axios.get(`${API}/hubspot/connections`);
  return response.data;
}

export async function fetchConnection(portalId) {
  const response = await axios.get(`${API}/hubspot/connections/${portalId}`);
  return response.data;
}

export async function startSync(portalId) {
  const response = await axios.post(`${API}/hubspot/connections/${portalId}/sync`);
  return response.data;
}

export async function recalculateAnnualArr(portalId, dealId) {
  const response = await axios.post(`${API}/hubspot/connections/${portalId}/deals/${dealId}/annual-arr/recalculate`);
  return response.data;
}

export async function previewAnnualArr(portalId, dealId) {
  const response = await axios.get(`${API}/hubspot/connections/${portalId}/deals/${dealId}/annual-arr/preview`);
  return response.data;
}

export async function fetchFilterOptions(portalId) {
  const response = await axios.get(`${API}/reports/filter-options`, {
    params: { portal_id: portalId },
  });
  return response.data;
}

export async function fetchReport(portalId, filters, fyStartMonth) {
  const response = await axios.get(`${API}/reports/mrr`, {
    params: {
      portal_id: portalId,
      fy_start_month: fyStartMonth || undefined,
      from_month: filters.fromMonth || undefined,
      to_month: filters.toMonth || undefined,
      stages: stringifyList(filters.stages),
      exclude_stages: stringifyList(filters.excludeStages),
      pipelines: stringifyList(filters.pipelines),
      owners: stringifyList(filters.owners),
      companies: stringifyList(filters.companies),
      products: stringifyList(filters.products),
      billing_frequencies: stringifyList(filters.billingFrequencies),
      include_one_time: filters.includeOneTime || undefined,
      collapse_products: filters.collapseProducts || undefined,
      exclude_non_xero: filters.excludeNonXero || undefined,
      probability_min: filters.probabilityMin || undefined,
      probability_max: filters.probabilityMax || undefined,
      close_date_from: filters.closeDateFrom || undefined,
      close_date_to: filters.closeDateTo || undefined,
      dynamic_filters: stringifyDynamic(filters.dynamicFilters),
      line_item_filters: stringifyDynamic(filters.lineItemFilters),
    },
  });
  return response.data;
}

export async function fetchMonthDetails(portalId, month, filters, changesOnly = false) {
  const response = await axios.get(`${API}/reports/mrr/${month}/details`, {
    params: {
      portal_id: portalId,
      from_month: filters.fromMonth || undefined,
      to_month: filters.toMonth || undefined,
      stages: stringifyList(filters.stages),
      exclude_stages: stringifyList(filters.excludeStages),
      pipelines: stringifyList(filters.pipelines),
      owners: stringifyList(filters.owners),
      companies: stringifyList(filters.companies),
      products: stringifyList(filters.products),
      billing_frequencies: stringifyList(filters.billingFrequencies),
      include_one_time: filters.includeOneTime || undefined,
      collapse_products: filters.collapseProducts || undefined,
      exclude_non_xero: filters.excludeNonXero || undefined,
      probability_min: filters.probabilityMin || undefined,
      probability_max: filters.probabilityMax || undefined,
      close_date_from: filters.closeDateFrom || undefined,
      close_date_to: filters.closeDateTo || undefined,
      dynamic_filters: stringifyDynamic(filters.dynamicFilters),
      line_item_filters: stringifyDynamic(filters.lineItemFilters),
      changes_only: changesOnly || undefined,
    },
  });
  return response.data;
}

// All line items active anywhere in the report window (whole FY), one per line.
export async function fetchWindowDetails(portalId, filters) {
  const response = await axios.get(`${API}/reports/mrr/window/details`, {
    params: {
      portal_id: portalId,
      from_month: filters.fromMonth || undefined,
      to_month: filters.toMonth || undefined,
      stages: stringifyList(filters.stages),
      exclude_stages: stringifyList(filters.excludeStages),
      pipelines: stringifyList(filters.pipelines),
      owners: stringifyList(filters.owners),
      companies: stringifyList(filters.companies),
      products: stringifyList(filters.products),
      billing_frequencies: stringifyList(filters.billingFrequencies),
      include_one_time: filters.includeOneTime || undefined,
      collapse_products: filters.collapseProducts || undefined,
      exclude_non_xero: filters.excludeNonXero || undefined,
      probability_min: filters.probabilityMin || undefined,
      probability_max: filters.probabilityMax || undefined,
      close_date_from: filters.closeDateFrom || undefined,
      close_date_to: filters.closeDateTo || undefined,
      dynamic_filters: stringifyDynamic(filters.dynamicFilters),
      line_item_filters: stringifyDynamic(filters.lineItemFilters),
    },
  });
  return response.data;
}

export async function fetchPresets(portalId) {
  const response = await axios.get(`${API}/reports/presets`, {
    params: { portal_id: portalId },
  });
  return response.data;
}

export async function createPreset(portalId, name, filters) {
  const response = await axios.post(`${API}/reports/presets`, {
    portal_id: portalId,
    name,
    filters,
  });
  return response.data;
}

export async function deletePreset(presetId) {
  const response = await axios.delete(`${API}/reports/presets/${presetId}`);
  return response.data;
}

export async function loadDealProperties(portalId, force = false) {
  const response = await axios.post(`${API}/hubspot/connections/${portalId}/load-properties?force=${force}`);
  return response.data;
}

// ── ARR per-portal config ─────────────────────────────────────────────────

export async function fetchPortalArrConfig(portalId) {
  const response = await axios.get(`${API}/hubspot/connections/${portalId}/arr-config`);
  return response.data;
}

export async function savePortalArrConfig(portalId, config) {
  const response = await axios.put(`${API}/hubspot/connections/${portalId}/arr-config`, config);
  return response.data;
}

export async function fetchDealPropertyDefs(portalId) {
  const response = await axios.get(`${API}/hubspot/connections/${portalId}/deal-properties`);
  return response.data;
}

export async function registerWorkflowAction() {
  const response = await axios.post(`${API}/hubspot/arr-automation/register-workflow-action`);
  return response.data;
}

export async function fetchArrDeals(portalId, product, filters) {
  const response = await axios.get(`${API}/reports/arr-deals`, {
    params: {
      portal_id: portalId,
      product,
      from_month: filters.fromMonth || undefined,
      to_month: filters.toMonth || undefined,
      stages: stringifyList(filters.stages),
      exclude_stages: stringifyList(filters.excludeStages),
      pipelines: stringifyList(filters.pipelines),
      owners: stringifyList(filters.owners),
      companies: stringifyList(filters.companies),
      products: stringifyList(filters.products),
      billing_frequencies: stringifyList(filters.billingFrequencies),
      include_one_time: filters.includeOneTime || undefined,
      collapse_products: filters.collapseProducts || undefined,
      exclude_non_xero: filters.excludeNonXero || undefined,
      probability_min: filters.probabilityMin || undefined,
      probability_max: filters.probabilityMax || undefined,
      close_date_from: filters.closeDateFrom || undefined,
      close_date_to: filters.closeDateTo || undefined,
      dynamic_filters: stringifyDynamic(filters.dynamicFilters),
      line_item_filters: stringifyDynamic(filters.lineItemFilters),
    },
  });
  return response.data;
}

export async function fetchChurnReport(portalId, filters) {
  const response = await axios.get(`${API}/reports/churn`, {
    params: {
      portal_id: portalId,
      from_month: filters.fromMonth || undefined,
      to_month: filters.toMonth || undefined,
      stages: stringifyList(filters.stages),
      exclude_stages: stringifyList(filters.excludeStages),
      pipelines: stringifyList(filters.pipelines),
      owners: stringifyList(filters.owners),
      companies: stringifyList(filters.companies),
      products: stringifyList(filters.products),
      billing_frequencies: stringifyList(filters.billingFrequencies),
      probability_min: filters.probabilityMin || undefined,
      probability_max: filters.probabilityMax || undefined,
      close_date_from: filters.closeDateFrom || undefined,
      close_date_to: filters.closeDateTo || undefined,
      dynamic_filters: stringifyDynamic(filters.dynamicFilters),
      line_item_filters: stringifyDynamic(filters.lineItemFilters),
    },
  });
  return response.data;
}

export async function fetchRevenueByClient(portalId, filters) {
  const response = await axios.get(`${API}/reports/revenue-by-client`, {
    params: {
      portal_id: portalId,
      from_month: filters.fromMonth || undefined,
      to_month: filters.toMonth || undefined,
      stages: stringifyList(filters.stages),
      exclude_stages: stringifyList(filters.excludeStages),
      pipelines: stringifyList(filters.pipelines),
      owners: stringifyList(filters.owners),
      companies: stringifyList(filters.companies),
      products: stringifyList(filters.products),
      billing_frequencies: stringifyList(filters.billingFrequencies),
      include_one_time: filters.includeOneTime || undefined,
      collapse_products: filters.collapseProducts || undefined,
      exclude_non_xero: filters.excludeNonXero || undefined,
      probability_min: filters.probabilityMin || undefined,
      probability_max: filters.probabilityMax || undefined,
      close_date_from: filters.closeDateFrom || undefined,
      close_date_to: filters.closeDateTo || undefined,
      dynamic_filters: stringifyDynamic(filters.dynamicFilters),
      line_item_filters: stringifyDynamic(filters.lineItemFilters),
    },
  });
  return response.data;
}

export async function exportIncomeStatement(portalId, filters, fyStartMonth) {
  const response = await axios.get(`${API}/reports/income-statement/${portalId}`, {
    params: {
      fy_start_month: fyStartMonth || undefined,
      stages: stringifyList(filters?.stages),
      exclude_stages: stringifyList(filters?.excludeStages),
      pipelines: stringifyList(filters?.pipelines),
      owners: stringifyList(filters?.owners),
      probability_min: filters?.probabilityMin || undefined,
      probability_max: filters?.probabilityMax || undefined,
    },
    responseType: "blob",
  });
  return response.data;
}

// Recognised revenue per client AND product for the filtered window, as CSV.
// The starting point for reconciling against the finance team's own schedule.
export async function exportRevenueByClientProduct(portalId, filters) {
  const response = await axios.get(`${API}/reports/revenue-by-client-product`, {
    params: {
      portal_id: portalId,
      format: "csv",
      from_month: filters.fromMonth || undefined,
      to_month: filters.toMonth || undefined,
      stages: stringifyList(filters.stages),
      exclude_stages: stringifyList(filters.excludeStages),
      pipelines: stringifyList(filters.pipelines),
      owners: stringifyList(filters.owners),
      companies: stringifyList(filters.companies),
      products: stringifyList(filters.products),
      billing_frequencies: stringifyList(filters.billingFrequencies),
      include_one_time: filters.includeOneTime || undefined,
      collapse_products: filters.collapseProducts || undefined,
      exclude_non_xero: filters.excludeNonXero || undefined,
      probability_min: filters.probabilityMin || undefined,
      probability_max: filters.probabilityMax || undefined,
      close_date_from: filters.closeDateFrom || undefined,
      close_date_to: filters.closeDateTo || undefined,
      dynamic_filters: stringifyDynamic(filters.dynamicFilters),
      line_item_filters: stringifyDynamic(filters.lineItemFilters),
    },
    responseType: "blob",
  });
  return response.data;
}

// ── Current user ──────────────────────────────────────────────────────────

export async function fetchMe() {
  const response = await axios.get(`${API}/auth/me`);
  return response.data;
}

// ── Portal settings (server-side, shared per portal) ─────────────────────

export async function fetchServerPortalSettings(portalId) {
  const response = await axios.get(`${API}/portal/${portalId}/settings`);
  return response.data;
}

export async function saveServerPortalSettings(portalId, settings) {
  const payload = {
    title: settings.title,
    subtitle: settings.subtitle,
    currency: settings.currency,
    decimal_places: settings.decimalPlaces,
    round_whole_numbers: settings.roundWholeNumbers,
    show_totals: settings.showTotals,
    show_legend: settings.showLegend,
    date_format: settings.dateFormat,
    arr_title: settings.arrTitle,
    arr_subtitle: settings.arrSubtitle,
    revenue_by_client_title: settings.revenueByClientTitle,
    revenue_by_client_subtitle: settings.revenueByClientSubtitle,
    palette: settings.palette,
    custom_colors: settings.customColors,
    visible_kpis: settings.visibleKpis,
    custom_metrics: settings.customMetrics,
    fiscal_year_start_month: settings.fiscalYearStartMonth,
  };
  const response = await axios.put(`${API}/portal/${portalId}/settings`, payload);
  return response.data;
}

// ── Client management ─────────────────────────────────────────────────────

export async function inviteClient(portalId, email) {
  const response = await axios.post(`${API}/admin/portal/${portalId}/invite`, { email: email || null });
  return response.data;
}

export async function listPortalClients(portalId) {
  const response = await axios.get(`${API}/admin/portal/${portalId}/clients`);
  return response.data;
}

export async function revokeClientAccess(portalId, email) {
  const response = await axios.delete(`${API}/admin/portal/${portalId}/clients/${encodeURIComponent(email)}`);
  return response.data;
}

export async function revokeInvite(portalId, inviteCode) {
  const response = await axios.delete(`${API}/admin/portal/${portalId}/invites/${encodeURIComponent(inviteCode)}`);
  return response.data;
}

export async function setClientRole(portalId, email, role) {
  const response = await axios.patch(`${API}/admin/portal/${portalId}/clients/${encodeURIComponent(email)}/role`, { role });
  return response.data;
}

// ── Stripe ────────────────────────────────────────────────────────────────

export async function createStripeCheckout(portalId = null, plan = "starter") {
  const response = await axios.post(`${API}/stripe/create-checkout`, { portal_id: portalId, plan });
  return response.data;
}

export async function fetchStripeBillingPortal() {
  const response = await axios.get(`${API}/stripe/billing-portal`);
  return response.data;
}

export async function verifyStripeSession(sessionId) {
  const response = await axios.post(`${API}/stripe/verify-session`, { session_id: sessionId });
  return response.data;
}

export async function fetchStripePortal(portalId) {
  const response = await axios.get(`${API}/stripe/portal/${portalId}`);
  return response.data;
}

export async function fetchPortalKpis(portalId, { pipelines = [], stages = [], revenueMode, collapseProducts, excludeNonXero } = {}, fyStartMonth) {
  const response = await axios.get(`${API}/portal/${portalId}/kpis`, {
    params: {
      pipelines: pipelines.length ? pipelines.join(",") : undefined,
      stages: stages.length ? stages.join(",") : undefined,
      fy_start_month: fyStartMonth || undefined,
      revenue_mode: revenueMode === "projected" ? "projected" : undefined,
      collapse_products: collapseProducts || undefined,
      exclude_non_xero: excludeNonXero || undefined,
    },
  });
  return response.data;
}

// ── Property configuration ────────────────────────────────────────────────

export async function fetchPropertyConfig(portalId) {
  const response = await axios.get(`${API}/portal/${portalId}/property-config`);
  return response.data;
}

export async function savePropertyConfig(portalId, selectedDealProps, selectedLiProps) {
  const response = await axios.put(`${API}/portal/${portalId}/property-config`, {
    selected_deal_props: selectedDealProps,
    selected_li_props: selectedLiProps,
  });
  return response.data;
}

// ── Report builder ────────────────────────────────────────────────────────
//
// Board-pack reports are stored definitions rather than code, so changing a
// filter is a save here, not a deploy. See backend report_builder_service.py.

export async function fetchReportBuilderMeta() {
  const response = await axios.get(`${API}/reports/builder/meta`);
  return response.data;
}

export async function fetchObjectProperties(portalId, objectType) {
  const response = await axios.get(`${API}/objects/${objectType}/properties`, {
    params: { portal_id: portalId },
  });
  return response.data;
}

export async function saveObjectPropertySelection(portalId, objectType, selected) {
  const response = await axios.put(
    `${API}/objects/${objectType}/properties/selection`,
    { selected },
    { params: { portal_id: portalId } },
  );
  return response.data;
}

export async function fetchReportDefinitions(portalId) {
  const response = await axios.get(`${API}/reports/definitions`, {
    params: { portal_id: portalId },
  });
  return response.data;
}

export async function saveReportDefinition(portalId, definition) {
  const response = await axios.post(`${API}/reports/definitions`, definition, {
    params: { portal_id: portalId },
  });
  return response.data;
}

export async function deleteReportDefinition(portalId, key) {
  const response = await axios.delete(`${API}/reports/definitions/${key}`, {
    params: { portal_id: portalId },
  });
  return response.data;
}

// `sections` optionally maps report key -> section name, to regroup reports.
export async function reorderReportDefinitions(portalId, keys, sections = undefined) {
  const response = await axios.post(
    `${API}/reports/definitions/reorder`,
    { keys, sections },
    { params: { portal_id: portalId } },
  );
  return response.data;
}

export async function runReportDefinition(
  portalId, key, force = false, fiscalYear = null, team = null,
) {
  const response = await axios.get(`${API}/reports/definitions/${key}/run`, {
    params: {
      portal_id: portalId,
      force: force || undefined,
      fiscal_year: fiscalYear ?? undefined,
      team: team ?? undefined,
    },
  });
  return response.data;
}

// Runs an unsaved definition so the builder can show the result while editing.
export async function previewReportDefinition(portalId, definition) {
  const response = await axios.post(`${API}/reports/definitions/preview`, definition, {
    params: { portal_id: portalId },
  });
  return response.data;
}

// Forces every seeded report back to its current definition. The way out when
// a report has drifted; presentation overrides such as a target survive.
export async function resetReportDefinitions(portalId) {
  const response = await axios.post(`${API}/reports/definitions/reset`, null, {
    params: { portal_id: portalId },
  });
  return response.data;
}

export async function fetchReconciliation(portalId, force = false, fiscalYear = null) {
  const response = await axios.get(`${API}/reports/reconciliation`, {
    params: { portal_id: portalId, force: force || undefined, fiscal_year: fiscalYear ?? undefined },
  });
  return response.data;
}

export async function fetchBoardPack(
  portalId,
  { force = false, fiscalYear = null, team = null } = {},
) {
  const response = await axios.get(`${API}/reports/board-pack`, {
    params: {
      portal_id: portalId,
      force: force || undefined,
      fiscal_year: fiscalYear ?? undefined,
      team: team ?? undefined,
    },
  });
  return response.data;
}

// Presentation-only update, so figures shown on a report card (the new
// business target above all) can be edited where they are read.
export async function updateReportDisplay(portalId, key, display) {
  const response = await axios.patch(`${API}/reports/definitions/${key}/display`, display, {
    params: { portal_id: portalId },
  });
  return response.data;
}

// Installs the board-pack starting definitions. Existing reports are left
// alone unless overwrite is set, so re-running never reverts an edit.
export async function seedReportDefinitions(portalId, overwrite = false) {
  const response = await axios.post(`${API}/reports/definitions/seed`, null, {
    params: { portal_id: portalId, overwrite: overwrite || undefined },
  });
  return response.data;
}

// The records behind a report, or behind one group of it — the drilldown that
// makes a board figure checkable rather than merely stated.
export async function fetchReportRecords(
  portalId,
  key,
  group = null,
  { limit = 500, fiscalYear = null, team = null } = {},
) {
  const response = await axios.get(`${API}/reports/definitions/${key}/records`, {
    params: {
      portal_id: portalId,
      group: group ?? undefined,
      limit,
      fiscal_year: fiscalYear ?? undefined,
      team: team ?? undefined,
    },
  });
  return response.data;
}

// ── Passwords ──────────────────────────────────────────────────────────────

export async function changePassword(currentPassword, newPassword, signOutOthers = true) {
  const response = await axios.post(`${API}/auth/change-password`, {
    current_password: currentPassword,
    new_password: newPassword,
    sign_out_others: signOutOthers,
  });
  // Signing out elsewhere replaces this device's token with a fresh one.
  if (response.data?.token) localStorage.setItem("auth_token", response.data.token);
  return response.data;
}

export async function adminSetPassword(email, password, requireChange = true, signOut = true) {
  const response = await axios.post(`${API}/admin/users/${encodeURIComponent(email)}/password`, {
    password,
    require_change: requireChange,
    sign_out: signOut,
  });
  return response.data;
}

export async function fetchPasswordRequests() {
  const response = await axios.get(`${API}/admin/password-requests`);
  return response.data;
}

export async function dismissPasswordRequest(email) {
  const response = await axios.post(
    `${API}/admin/password-requests/${encodeURIComponent(email)}/dismiss`,
  );
  return response.data;
}
